import { message } from 'antd';
import { useCallback, useRef, useState } from 'react';

import useSubscription from '@/hooks/useSubscription';
import { fileToBase64, uuid } from '@/utils/common';
import handleUpload from '@/utils/upload';

import { CONTAINER_EVENT } from '../../utils/utils';

interface IReturn {
  upList: FileItem[];
  handleUploadChange(e: React.ChangeEvent<HTMLInputElement>): void;
  handleUploadClick(e: React.MouseEvent<HTMLInputElement>): void;
  handleDelFile(index: string): void;
  clearUpList(): void;
  handleUploadFiles(fileList: FileList | never[], e?: React.ChangeEvent<HTMLInputElement>): void;
}

export enum UpStatus {
  LOADING = 'LOADING',
  SUCCESS = 'SUCCESS',
  FAIL = 'FAIL'
}

export interface FileItem {
  id: string;
  type: string;
  name: string;
  link: string;
  status: UpStatus;
}

function useUpload(userId?: string | number, uploadType?: string, maxFiles: number = 10): IReturn {
  const overMaxFilesTips = `Too many files uploaded. Maximum number of files at a time is ${maxFiles}`;
  const [upList, setUpList] = useState<FileItem[]>([]);
  const upListRef = useRef<FileItem[]>(upList);
  const maxFileMBSize = useSubscription((state) => state.computed.maxFileMBSize);

  const updateUpList = (files: FileItem[]) => {
    setUpList(files);
    upListRef.current = files;
  };

  const handleDelFile = useCallback(
    (id: string) => {
      const nextList = upList.filter((file: FileItem) => file.id != id);

      updateUpList(nextList);
    },
    [upList]
  );

  const clearUpList = () => {
    updateUpList([]);
  };

  const handleUploadClick = useCallback(
    (e: React.MouseEvent<HTMLInputElement>) => {
      // TODO userId
      if (!userId) {
        e.preventDefault();
        dispatchEvent(new CustomEvent(CONTAINER_EVENT.NEED_LOGIN));
      }
    },
    [userId]
  );

  const handleUploadFile = useCallback(
    (file: File, id: string) => {
      return new Promise<void>((resolve, reject) => {
        const path = `users/send/${file.type.startsWith('image/') ? 'img' : 'file'}`;
        const filePrefix = `${Date.now()}_${userId}`;

        if (uploadType && !uploadType.includes(file.type)) {
          reject('This file type is not supported.');

          return;
        }

        handleUpload(file, path, filePrefix)
          .then((link) => {
            const nextList = upListRef.current;

            upListRef.current.some((item: FileItem, index: number) => {
              if (item.id == id) {
                nextList[index] = { ...item, status: UpStatus.SUCCESS, link };

                return true;
              }

              return false;
            });

            updateUpList([...nextList]);
            resolve();
          })
          .catch(() => {
            // handleDelFile(0);
            reject('upload error');
          });
      });
    },
    [userId, uploadType]
  );

  const handleUploadFiles = useCallback(
    (fileList: FileList | never[], e?: React.ChangeEvent<HTMLInputElement>) => {
      if (upListRef.current.length >= maxFiles) {
        message.error(overMaxFilesTips);

        return;
      }

      let currentNum = upListRef.current.length;

      for (const file of fileList) {
        if (currentNum >= maxFiles) {
          message.error(overMaxFilesTips);

          return;
        }

        if (file.size / 1024 / 1024 > maxFileMBSize) {
          message.error(
            `The file is too large. Please select a file that is no more than ${maxFileMBSize}MB.`
          );

          continue;
        }

        if (uploadType && !uploadType.includes(file.type)) {
          message.error('This file type is not supported.');

          continue;
        }

        currentNum++;

        const id = uuid();

        fileToBase64(file)
          .then((base64) => {
            upListRef.current.push({
              id,
              type: file.type,
              name: file.name,
              link: base64 as string,
              status: UpStatus.LOADING
            });
          })
          .catch(() => {
            upListRef.current.push({
              id,
              type: file.type,
              name: file.name,
              link: '',
              status: UpStatus.LOADING
            });
          })
          .finally(() => {
            updateUpList([...upListRef.current]);
          });

        handleUploadFile(file, id)
          .catch((err) => {
            updateUpList(upListRef.current.filter((item) => item.id !== id));
            message.error(err);
          })
          .finally(() => {
            if (e) {
              e.target.value = '';
            }
          });
      }
    },
    [handleUploadFile, maxFileMBSize, maxFiles, overMaxFilesTips, uploadType]
  );

  const handleUploadChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.target.files || [];

      if (!fileList.length) {
        return;
      }

      handleUploadFiles(fileList, e);
    },
    [handleUploadFiles]
  );

  return {
    upList,
    handleUploadChange,
    handleUploadClick,
    handleDelFile,
    clearUpList,
    handleUploadFiles
  };
}

export default useUpload;
